import { Button, IconButton } from "../../../design-system";
import React from "react";
import {
  HiOutlinePause,
  HiOutlinePlay,
  HiOutlineSpeakerWave,
  HiOutlineSpeakerXMark,
} from "react-icons/hi2";
import { AlbumData, TrackData } from "../Playlist.types";

type BottomTab = {
  icon: React.ReactNode;
  label: string;
};

type PlayerFooterProps = {
  variant: "desktop" | "mobile";
  albumData: AlbumData;
  primaryTrack?: TrackData | null;
  isPlaying: boolean;
  currentTimeSec: number;
  displayedDurationSec: number;
  progressPercent: number;
  bottomTabs: BottomTab[];
  isDesktopSeekHoverVisible: boolean;
  desktopSeekHoverPercent: number;
  desktopSeekHoverTimeSec: number;
  desktopSeekBarRef: React.RefObject<HTMLDivElement>;
  mobileSeekBarRef: React.RefObject<HTMLDivElement>;
  onPlayPause: () => void;
  onPreviousTrack: () => void;
  onNextTrack: () => void;
  isMuted: boolean;
  onToggleMute: () => void;
  onAction: () => void;
  onDesktopSeekMouseEnter: () => void;
  onDesktopSeekMouseLeave: () => void;
  onDesktopSeekMouseMove: (event: React.MouseEvent<HTMLDivElement>) => void;
  onDesktopSeekClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMobileSeekClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  onBottomTabClick: (tabLabel: string) => void;
  activeBottomTabLabel: string;
  formatDuration: (durationSec?: number) => string;
  audioElement: React.ReactNode;
};

function PlayerFooter({
  variant,
  albumData,
  primaryTrack,
  isPlaying,
  currentTimeSec,
  displayedDurationSec,
  progressPercent,
  bottomTabs,
  isDesktopSeekHoverVisible,
  desktopSeekHoverPercent,
  desktopSeekHoverTimeSec,
  desktopSeekBarRef,
  mobileSeekBarRef,
  onPlayPause,
  onPreviousTrack,
  onNextTrack,
  isMuted,
  onToggleMute,
  onAction,
  onDesktopSeekMouseEnter,
  onDesktopSeekMouseLeave,
  onDesktopSeekMouseMove,
  onDesktopSeekClick,
  onMobileSeekClick,
  onBottomTabClick,
  activeBottomTabLabel,
  formatDuration,
  audioElement,
}: PlayerFooterProps) {
  if (variant === "desktop") {
    return (
      <footer className="fixed bottom-0 left-0 right-0 h-20 bg-player-surface border-t border-player-border px-5">
        <div
          ref={desktopSeekBarRef}
          onMouseEnter={onDesktopSeekMouseEnter}
          onMouseLeave={onDesktopSeekMouseLeave}
          onMouseMove={onDesktopSeekMouseMove}
          onClick={onDesktopSeekClick}
          className="relative h-4 flex items-center cursor-pointer"
        >
          <div className="h-1 w-full bg-player-border/60 rounded-full overflow-hidden">
            <div
              className="h-full bg-progress transition-all duration-200"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {isDesktopSeekHoverVisible && displayedDurationSec > 0 ? (
            <div
              className="absolute -top-7 -translate-x-1/2 rounded bg-player-raised/95 px-2 py-0.5 text-eyebrow text-player-text"
              style={{ left: `${desktopSeekHoverPercent}%` }}
            >
              {formatDuration(Math.round(desktopSeekHoverTimeSec))}
            </div>
          ) : null}
        </div>

        <div className="h-full flex items-center justify-between gap-5 pb-3">
          {/* Left PANEL */}
          <div className="flex items-center gap-4 min-w-0">
            <IconButton label="Previous track"
              onClick={onPreviousTrack}
              className="text-3xl text-player-text"
            >
              <span>⏮</span>
            </IconButton>
            <IconButton label={isPlaying ? "Pause" : "Play"} onClick={onPlayPause} className="text-3xl text-player-text">
              <span>{isPlaying ? "⏸" : "▶"}</span>
            </IconButton>
            <IconButton label="Next track" onClick={onNextTrack} className="text-3xl text-player-text">
              <span>⏭</span>
            </IconButton>
            <span className="text-player-muted text-sm">
              {formatDuration(Math.floor(currentTimeSec))} /{" "}
              {formatDuration(displayedDurationSec)}
            </span>
          </div>

          {/* Center PANEL album info */}
          <div className="flex items-center gap-3 min-w-0 flex-1 max-w-xl">
            <img
              src={albumData.coverUrl || "images/album_inner.jpg"}
              alt="Track"
              className="h-12 w-12 rounded object-cover"
            />
            <div className="truncate">
              <p className="text-sm truncate">
                {primaryTrack?.title || albumData.title}
              </p>
              <p className="text-xs text-player-muted truncate">
                {primaryTrack?.artistName || albumData.artistName || "Artista"}{" "}
                • {albumData.title} • {albumData.year}
              </p>
            </div>
          </div>

          {/* Right PANEL */}
          <div className="flex items-center gap-4 text-player-secondary text-2xl">
            <IconButton label={isMuted ? "Unmute" : "Mute"} onClick={onToggleMute}>
              {isMuted ? (
                <HiOutlineSpeakerXMark size={22} />
              ) : (
                <HiOutlineSpeakerWave size={22} />
              )}
            </IconButton>
            {/* <Button variant="ghost" size="none" onClick={onAction}>🔀</Button>
            <Button variant="ghost" size="none" onClick={onAction}>🔁</Button>
            <Button variant="ghost" size="none" onClick={onAction}>▴</Button> */}
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="fixed bottom-0 left-0 right-0">
      <div
        ref={mobileSeekBarRef}
        onClick={onMobileSeekClick}
        className="mx-3 mb-1 h-4 flex items-center cursor-pointer"
      >
        <div className="h-1 w-full rounded-full bg-player-border/60 overflow-hidden">
          <div
            className="h-full bg-progress transition-all duration-200"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
      <div className="mx-3 mb-1 rounded-md bg-player-surface px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <img
            src={albumData.coverUrl || "images/album_inner.jpg"}
            alt="Mini player"
            className="h-10 w-10 rounded object-cover"
          />
          <div className="min-w-0">
            <p className="text-sm truncate">
              {primaryTrack?.title || albumData.title}
            </p>
            <p className="text-xs text-player-muted truncate">
              {primaryTrack?.artistName || albumData.artistName || "Artista"}
            </p>
            <p className="text-eyebrow text-player-subtle truncate">
              {formatDuration(Math.floor(currentTimeSec))} /{" "}
              {formatDuration(displayedDurationSec)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-2xl">
          <Button variant="ghost" size="none" onClick={onAction}>📺</Button>
          <IconButton label={isPlaying ? "Pause" : "Play"} onClick={onPlayPause}>
            {isPlaying ? (
              <HiOutlinePause size={23} />
            ) : (
              <HiOutlinePlay size={23} />
            )}
          </IconButton>
        </div>
      </div>

      {audioElement}

      <div className="h-20 bg-player-bottom border-t border-player-raised px-6 flex items-center justify-between">
        {bottomTabs.map((tab) => (
          <Button variant="ghost" size="none"
            key={tab.label}
            onClick={() => onBottomTabClick(tab.label)}
            className={`flex flex-col items-center ${
              tab.label === activeBottomTabLabel
                ? "text-white"
                : "text-control-hover"
            }`}
          >
            {tab.icon}
            <span className="text-xs mt-1">{tab.label}</span>
          </Button>
        ))}
      </div>
    </footer>
  );
}

export default PlayerFooter;
